import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { UserCircle, EnvelopeSimple, Briefcase, Phone, Buildings, IdentificationCard, ArrowClockwise, LockKey, Eye, EyeSlash, DownloadSimple } from '@phosphor-icons/react';
import { useAuth } from '../App';
import UserAssets from './UserAssets';
import { ModuleShell, SkeletonBlock } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
import { notifySuccess, notifyError } from '../lib/notify';
const API = apiPath('/api/users');
const AUTH = apiPath('/api/auth');
const DEV_SYNC = apiPath('/api/dev/backup-sync');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function roleDisplay(role) {
  const labels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    manager: 'Manager',
    lead: 'Lead',
    member: 'Member',
    client: 'Client',
    agent: 'Agent',
    pending: 'Pending Approval',
    end_user: 'Client',
  };
  return labels[role] || role?.replace('_', ' ');
}

function ProfilePage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: '',
    designation: '',
    phone: '',
    department: '',
    employee_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [devSync, setDevSync] = useState(null);
  const [sshPassword, setSshPassword] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [saveBackupFile, setSaveBackupFile] = useState(true);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState(null);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  useEffect(() => {
    axios.get(`${API}/me/profile`, authHeaders())
      .then((res) => {
        const u = res.data;
        setForm({
          name: u.name || '',
          designation: u.designation || '',
          phone: u.phone || '',
          department: u.department || '',
          employee_id: u.employee_id || '',
        });
      })
      .catch(() => setError('Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  const refreshDevSync = useCallback(() => {
    if (user?.role !== 'super_admin') return;
    axios.get(`${DEV_SYNC}/status`, authHeaders())
      .then((res) => setDevSync(res.data?.enabled ? res.data : null))
      .catch(() => setDevSync(null));
  }, [user?.role]);

  useEffect(() => {
    refreshDevSync();
  }, [refreshDevSync]);

  const downloadSavedBackup = async (preferredName) => {
    const res = await axios.get(`${DEV_SYNC}/download`, {
      ...authHeaders(),
      responseType: 'blob',
      timeout: 600000,
    });
    const cd = res.headers?.['content-disposition'] || '';
    const match = cd.match(/filename="?([^"]+)"?/i);
    const filename = preferredName || match?.[1] || 'workflowdesk-live.mongo.archive.gz';
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  };

  const runLiveBackupSync = async () => {
    if (!devSync?.enabled || syncing) return;
    const ok = window.confirm(
      'This replaces your LOCAL database with the latest live backup.\n\n'
      + (saveBackupFile ? 'The backup file will also download to this computer.\n\n' : '')
      + 'You may need to log in again afterwards. Continue?',
    );
    if (!ok) return;
    setSyncing(true);
    setError(null);
    try {
      const res = await axios.post(
        `${DEV_SYNC}/run`,
        { ssh_password: sshPassword },
        { ...authHeaders(), timeout: 600000 },
      );
      const tickets = res.data?.tickets ?? '—';
      const users = res.data?.users ?? '—';
      let extra = '';
      if (saveBackupFile) {
        try {
          const name = await downloadSavedBackup(res.data?.saved_filename);
          extra = ` Backup file saved: ${name}`;
        } catch (dlErr) {
          extra = ' Database restored, but the backup file download failed — use “Download last backup file”.';
        }
      }
      notifySuccess(`Local DB restored from live (${tickets} tickets, ${users} users).${extra} Refresh the app.`);
      setSshPassword('');
      refreshDevSync();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      notifyError(typeof detail === 'string' ? detail : 'Live backup sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const runDownloadOnly = async () => {
    if (downloadingBackup) return;
    setDownloadingBackup(true);
    try {
      const name = await downloadSavedBackup(devSync?.last_backup?.filename);
      notifySuccess(`Saved ${name} to your Downloads folder.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      notifyError(typeof detail === 'string' ? detail : 'No saved backup file to download yet.');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
        setError(null);
    try {
      const res = await axios.patch(`${API}/me/profile`, form, authHeaders());
      notifySuccess('Profile saved successfully');
      if (setUser) setUser({ ...user, ...res.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    if ((pwForm.next || '').length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${AUTH}/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        authHeaders(),
      );
      notifySuccess('Password updated. Use it the next time you sign in.');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Could not update password');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <ModuleShell
        title="My Profile"
        subtitle="Update your contact and job details. Email is managed by your sign-in account."
      >
        <SkeletonBlock />
      </ModuleShell>
    );
  }

  const isPending = user?.role === 'pending';

  return (
    <ModuleShell
      title="My Profile"
      subtitle="Update your contact and job details. Email is managed by your sign-in account."
    >
    <div style={{ maxWidth: '640px' }}>

      {isPending && (
        <div style={{ padding: '14px 16px', background: '#FEF3C7', borderRadius: '10px', marginBottom: '8px', fontSize: '12px', color: '#92400E' }}>
          Your account is awaiting role assignment. Complete your profile below — a super admin will assign your role shortly.
        </div>
      )}
      {error && <div style={alertErr}>{error}</div>}

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          {user?.picture ? (
            <img src={user.picture} alt="" style={{ width: '72px', height: '72px', borderRadius: '50%' }} />
          ) : (
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%', background: '#002FA7', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '600',
            }}
            >
              {(form.name || user?.email || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{form.name || user?.name}</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>{user?.email}</div>
            <span style={roleBadge}>{roleDisplay(user?.role)}</span>
          </div>
        </div>

        <form onSubmit={save}>
          <ProfileField icon={EnvelopeSimple} label="Email">
            <input value={user?.email || ''} disabled style={{ ...inputStyle, background: '#F9FAFB', color: '#6B7280' }} />
          </ProfileField>

          <ProfileField icon={UserCircle} label="Full name" required>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Your full name"
              style={inputStyle}
            />
          </ProfileField>

          <ProfileField icon={Briefcase} label="Designation" hint="Job title — required for staff team assignment">
            <input
              value={form.designation}
              onChange={(e) => setForm({ ...form, designation: e.target.value })}
              placeholder="e.g. Senior Support Engineer"
              style={inputStyle}
            />
          </ProfileField>

          <ProfileField icon={Phone} label="Mobile number">
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="e.g. +91 98765 43210"
              type="tel"
              style={inputStyle}
            />
          </ProfileField>

          <ProfileField icon={Buildings} label="Department">
            <input
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              placeholder="e.g. IT Support"
              style={inputStyle}
            />
          </ProfileField>

          <ProfileField icon={IdentificationCard} label="Employee ID">
            <input
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              placeholder="Optional employee / badge number"
              style={inputStyle}
            />
          </ProfileField>

          <button type="submit" disabled={saving} style={{ ...saveBtn, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </div>

      {user?.role === 'super_admin' && (
        <div style={{ ...cardStyle, marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <LockKey size={18} color="#002FA7" />
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Change password</div>
          </div>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 16px', lineHeight: 1.5 }}>
            Updates the super admin login password for this account ({user?.email}). You will use the new password the next time you sign in.
          </p>
          {pwError && <div style={alertErr}>{pwError}</div>}
          <form onSubmit={savePassword}>
            <ProfileField icon={LockKey} label="Current password" required>
              <PasswordInput
                value={pwForm.current}
                onChange={(v) => setPwForm({ ...pwForm, current: v })}
                visible={showPw.current}
                onToggle={() => setShowPw({ ...showPw, current: !showPw.current })}
                autoComplete="current-password"
              />
            </ProfileField>
            <ProfileField icon={LockKey} label="New password" required hint="At least 8 characters">
              <PasswordInput
                value={pwForm.next}
                onChange={(v) => setPwForm({ ...pwForm, next: v })}
                visible={showPw.next}
                onToggle={() => setShowPw({ ...showPw, next: !showPw.next })}
                autoComplete="new-password"
              />
            </ProfileField>
            <ProfileField icon={LockKey} label="Confirm new password" required>
              <PasswordInput
                value={pwForm.confirm}
                onChange={(v) => setPwForm({ ...pwForm, confirm: v })}
                visible={showPw.confirm}
                onToggle={() => setShowPw({ ...showPw, confirm: !showPw.confirm })}
                autoComplete="new-password"
              />
            </ProfileField>
            <button type="submit" disabled={pwSaving} style={{ ...saveBtn, opacity: pwSaving ? 0.7 : 1 }}>
              {pwSaving ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </div>
      )}

      {user?.user_id && (
        <div style={{ ...cardStyle, marginTop: '20px' }}>
          <UserAssets userId={user.user_id} title="My Assigned Assets" />
        </div>
      )}

      {devSync?.enabled && user?.role === 'super_admin' && (
        <div style={{ ...cardStyle, marginTop: '20px', borderColor: '#99F6E4', background: '#F0FDFA' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F766E', marginBottom: 6 }}>
            Local development
          </div>
          <p style={{ fontSize: 12, color: '#374151', margin: '0 0 12px', lineHeight: 1.5 }}>
            Fetch the latest live Mongo backup from {devSync.host || 'the server'} and restore it
            into this local database ({devSync.ns_to || 'workflowdesk_dev'}).
            The archive is kept in the project folder
            {' '}<code style={{ fontSize: 11 }}>daily-backups/synced-from-live/</code>
            {' '}and, if checked below, also downloaded to this computer.
            This control is not available on production.
          </p>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Live server SSH password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            value={sshPassword}
            onChange={(e) => setSshPassword(e.target.value)}
            placeholder="root password for the live droplet"
            style={{ ...inputStyle, marginBottom: 12, background: '#fff' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={saveBackupFile}
              onChange={(e) => setSaveBackupFile(e.target.checked)}
            />
            Also save the backup file to this computer (Downloads)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              disabled={syncing}
              onClick={runLiveBackupSync}
              style={{
                ...saveBtn,
                marginTop: 0,
                background: '#0F766E',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                opacity: syncing ? 0.7 : 1,
              }}
            >
              <ArrowClockwise size={16} weight="bold" />
              {syncing ? 'Syncing live backup…' : 'Sync live backup to local'}
            </button>
            <button
              type="button"
              disabled={downloadingBackup || !devSync.last_backup}
              onClick={runDownloadOnly}
              style={{
                ...saveBtn,
                marginTop: 0,
                background: '#fff',
                color: '#0F766E',
                border: '1px solid #99F6E4',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                opacity: (downloadingBackup || !devSync.last_backup) ? 0.6 : 1,
              }}
            >
              <DownloadSimple size={16} weight="bold" />
              {downloadingBackup ? 'Downloading…' : 'Download last backup file'}
            </button>
          </div>
          {devSync.last_backup ? (
            <p style={{ fontSize: 11, color: '#0F766E', margin: '10px 0 0' }}>
              Last saved: {devSync.last_backup.filename}
              {devSync.last_backup.size_bytes
                ? ` (${Math.max(1, Math.round(devSync.last_backup.size_bytes / (1024 * 1024)))} MB)`
                : ''}
            </p>
          ) : null}
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

function PasswordInput({ value, onChange, visible, onToggle, autoComplete }) {
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={autoComplete}
        style={{ ...inputStyle, paddingRight: 44 }}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={visible ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280',
        }}
      >
        {visible ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

function ProfileField({ icon: Icon, label, required, hint, children }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
        <Icon size={18} weight="regular" color="#6B7280" />
        {label}{required ? ' *' : ''}
      </label>
      {children}
      {hint && <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

const cardStyle = {
  background: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '28px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '12px',
  boxSizing: 'border-box',
};

const saveBtn = {
  marginTop: '8px',
  padding: '12px 28px',
  background: '#002FA7',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontWeight: '600',
  fontSize: '12px',
  cursor: 'pointer',
};

const roleBadge = {
  display: 'inline-block',
  marginTop: '8px',
  padding: '4px 10px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: '600',
  background: '#EFF6FF',
  color: '#1D4ED8',
};

const alertOk = { padding: '6px 10px', background: '#D1FAE5', borderRadius: '8px', marginBottom: '8px', color: '#065F46', fontSize: '12px' };
const alertErr = { padding: '6px 10px', background: '#FEE2E2', borderRadius: '8px', marginBottom: '8px', color: '#B91C1C', fontSize: '12px' };

export default ProfilePage;
